import { Typography } from "@mui/material";

import { HighlightedDetailField } from "../../../components";
import { formatTime } from "../../../components/HubHookFormTimePicker/helper";
import { useStationListWithCache } from "../../../components/StationHookFormSelect";

import { FieldGrid, TableBodyRow, TableHeaderRow, TableShell } from "../styles";
import { BilingualTitle, Section, SectionSubHeader } from "../components";

const injections = [
  "Hand Injection",
  "Side Injection",
  "Top Injection",
  "Automatic Injection",
];
const cupTypes = ["Ounces", "Regular", "Filter"];

const WAX_TABLE_COLS = "60px 1fr 1fr 1fr 1fr";
const WaxRoutingTable = ({ routing = [], stations = [] }) => (
  <TableShell>
    <TableHeaderRow columns={WAX_TABLE_COLS}>
      <BilingualTitle label="#" spanishLabel="#" size="sm" />
      <BilingualTitle label="Station" spanishLabel="Estación" size="sm" />
      <BilingualTitle
        label="Lead Time"
        spanishLabel="Tiempo de Espera"
        size="sm"
      />
      <BilingualTitle
        label="Cycle Time"
        spanishLabel="Tiempo de Ciclo"
        size="sm"
      />
      <BilingualTitle label="Notes" spanishLabel="Notas" size="sm" />
    </TableHeaderRow>
    {routing.map((r, idx) => {
      const station = r?.stationId
        ? stations.find((s) => s.id === r.stationId)
        : null;
      return (
        <TableBodyRow key={r.id ?? idx} columns={WAX_TABLE_COLS}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {idx + 1}.
          </Typography>
          <Typography variant="caption">{station?.label ?? "—"}</Typography>
          <Typography variant="caption">
            {Math.round(r?.leadTime / 1440)}
          </Typography>
          <Typography variant="caption">
            {Math.round(r?.cycleTime / 1440)}
          </Typography>
          <Typography variant="caption">{r?.notes || "—"}</Typography>
        </TableBodyRow>
      );
    })}
  </TableShell>
);

export const PartWaxDetails = ({ section = {} }) => {
  const config = section?.config || {};
  const routing = section?.routing || [];
  const { stations = [] } = useStationListWithCache();

  const singleRoute = routing.length === 1 ? routing[0] : null;
  const singleStation = singleRoute?.stationId
    ? stations.find((s) => s.id === singleRoute.stationId)
    : null;

  return (
    <Section icon="Layers" label="Wax Department" spanishLabel="Cera">
      {routing.length > 1 ? (
        <WaxRoutingTable routing={routing} stations={stations} />
      ) : (
        <FieldGrid columns={3}>
          <HighlightedDetailField
            label="Station"
            spanishLabel="Estación"
            value={singleStation?.label}
          />
          <HighlightedDetailField
            label="Injection Type"
            spanishLabel="Tipo de Inyección"
            value={injections[config?.injectType]}
          />
          <HighlightedDetailField
            label="Mold #"
            spanishLabel="Molde #"
            value={config?.moldNumber}
          />
        </FieldGrid>
      )}

      <HighlightedDetailField
        label="Notes"
        spanishLabel="Notas"
        value={section?.notes}
      />

      <SectionSubHeader
        label="Wax Injection"
        spanishLabel="Inyección de Cera"
      />
      <FieldGrid columns={3}>
        <HighlightedDetailField
          label="Wax Temp"
          spanishLabel="Temperatura de Cera"
          value={config?.waxInjectionTemp}
          endAdornment="°F"
        />
        <HighlightedDetailField
          label="Top Platten Temp"
          spanishLabel="Temp. Superior del Platten"
          value={config?.topPlattenTemp}
          endAdornment="°F"
        />
        <HighlightedDetailField
          label="Bottom Platten Temp"
          spanishLabel="Temp. Inferior del Platten"
          value={config?.bottomPlattenTemp}
          endAdornment="°F"
        />
      </FieldGrid>
      <FieldGrid columns={4}>
        <HighlightedDetailField
          label="Injection Pressure"
          spanishLabel="Presión de Inyección"
          value={config?.waxInjectionPressure}
          endAdornment="psi"
        />
        <HighlightedDetailField
          label="Injection Time"
          spanishLabel="Tiempo de Inyección"
          value={formatTime(config?.injectionTime)}
        />
        <HighlightedDetailField
          label="Flow Rate"
          spanishLabel="Tasa de Flujo"
          value={config?.flowRate}
          endAdornment="g/s"
        />
        <HighlightedDetailField
          label="Hold Time"
          spanishLabel="Tiempo de Sostenimiento"
          value={formatTime(config?.holdTime)}
        />
      </FieldGrid>

      <SectionSubHeader
        label="Wax Setup"
        spanishLabel="Configuración de Cera"
      />
      <FieldGrid columns={2}>
        <HighlightedDetailField
          label="Cup Size"
          spanishLabel="Tamaño del Vaso"
          value={config?.cupSize}
        />
        <HighlightedDetailField
          label="Cup Type"
          spanishLabel="Tipo de Vaso"
          value={cupTypes[config?.cupType - 1]}
        />
      </FieldGrid>
    </Section>
  );
};

export default PartWaxDetails;
